import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Notification } from "../../../../@types/Notification";
import { ISectionProps } from "../../../../@types/Patient";
import { ChevronDown } from "../../../../assets/icons/ChevronDown";
import { ChevronUp } from "../../../../assets/icons/ChevronUp";
import { CustomIconButton } from "../../../../components/CustomIconButton";
import { getUserAppointmentsData } from "../../../../hooks/queries/appointments";
import { useModal } from "../../../../hooks/useModal";
import { useToast } from "../../../../hooks/useToast";
import { PageTitle } from "../../../../layout/components/PageTitle";
import { mixpanelActions } from "../../../../utils/mixpanel";
import { AppointmentsTable } from "../../../Appointments/components/AppointmentsTable";
import { CalendarCustomOptionsBar } from "../../../Appointments/components/CalendarOptionsBar";
import { calendarDateFilterOptions } from "../../../Appointments/components/CalendarOptionsBar/calendarDateFilterOptions";
import { CreateAppointmentModal } from "../../../Appointments/components/CreateAppointmentModal";
import { DateFilterOptionType } from "../MeasurementModal/components/CustomOptionsBar";
import {
  CreateAppointmentContainer,
  Panel,
  Subtitle,
  TableBleed,
  TitleContainer,
  TitleLeft,
} from "./AppointmentsSection.styles";

export function AppointmentsSection({
  patient,
  updateColapsedSections,
  collapsedSection,
}: ISectionProps) {
  const { t } = useTranslation();

  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();

  const currentDate = dayjs();
  const currentMonthIndex = currentDate.month();
  const [selectedFilterDate, setSelectedFilterDate] =
    useState<DateFilterOptionType>({
      endDate: currentDate.endOf("month"),
      id: calendarDateFilterOptions[currentMonthIndex].id,
      startDate:
        calendarDateFilterOptions[currentMonthIndex].value(currentDate),
    });

  const { appointments, isFetching, isRefetching, refetch } =
    getUserAppointmentsData({
      startDate: selectedFilterDate.startDate.toISOString(),
      endDate: selectedFilterDate.endDate.toISOString(),
      patientId: patient.id,
    });

  useEffect(() => {
    refetch();
  }, [selectedFilterDate]);

  const handleOnClose = (
    notification?: Notification,
    refetchTable?: boolean
  ) => {
    if (notification !== undefined) {
      showToast(notification.message, notification.type);
    }

    if (refetchTable) {
      refetch();
    }

    closeModal();
  };

  const handleCreateAppointment = () => {
    openModal(
      <CreateAppointmentModal
        onClose={handleOnClose}
        prefilledPatientId={patient.id}
      />,
      {
        width: "100%",
        height: "100%",
        maxHeight: "99vh",
        showCloseButton: true,
      }
    );
  };

  return (
    <>
      <TitleContainer>
        <TitleLeft>
          <PageTitle>{t("patientPage_appointments_title")}</PageTitle>
          <CustomIconButton
            style={{ marginLeft: "2px" }}
            onClick={() => updateColapsedSections("appointmentsSection")}
          >
            {collapsedSection.value ? <ChevronUp /> : <ChevronDown />}
          </CustomIconButton>
        </TitleLeft>
      </TitleContainer>

      {!collapsedSection.value && (
        <CreateAppointmentContainer
          onClick={() => {
            handleCreateAppointment();
            mixpanelActions.track("User Action: CreateAppointmentButton");
          }}
        >
          {t("patientPage_appointments_create")}
        </CreateAppointmentContainer>
      )}

      <div
        style={{
          height: collapsedSection.value ? "0" : "",
          overflow: collapsedSection.value ? "hidden" : "",
        }}
      >
        <Panel>
          <Subtitle>{`${selectedFilterDate.startDate.format(
            "MMM D, YYYY"
          )} - ${selectedFilterDate.endDate.format("MMM D, YYYY")}`}</Subtitle>

          <CalendarCustomOptionsBar
            onChange={(selectedDateFilter) =>
              setSelectedFilterDate(selectedDateFilter)
            }
            selectedFilterDate={selectedFilterDate}
          />

          <TableBleed>
            <AppointmentsTable
              onAppointmentDelete={handleOnClose}
              appointments={appointments}
              isLoading={isFetching || isRefetching}
              bordered={false}
              emptyStateMessage={t("patientPage_appointments_empty")}
            />
          </TableBleed>
        </Panel>
      </div>
    </>
  );
}
